import { motion, AnimatePresence } from 'framer-motion';
import { useStoryStore } from '../../store/storyStore';
import { TypewriterText } from './TypewriterText';
import { NarrativeType } from '../../types/story.types';

/**
 * DialogueBox component
 * Displays the current narrative with character information
 */
export function DialogueBox() {
  const currentNarrative = useStoryStore((state) => state.currentNarrative);
  const isDialogueBoxVisible = useStoryStore((state) => state.isDialogueBoxVisible);
  const canAdvance = useStoryStore((state) => state.canAdvance);
  const advanceNarrative = useStoryStore((state) => state.advanceNarrative);

  if (!isDialogueBoxVisible || !currentNarrative) {
    return null;
  }

  const narrativeType = currentNarrative.type as NarrativeType;
  const isDialogue = narrativeType === NarrativeType.Dialogue;
  const isThought = narrativeType === NarrativeType.InnerThought;
  const isNarration = narrativeType === NarrativeType.Narration;

  // Get box styling based on narrative type
  const getBoxStyle = () => {
    if (isThought) {
      return 'border-purple-400/30 bg-purple-900/20';
    }
    if (isNarration) {
      return 'border-blue-400/30 bg-blue-900/20';
    }
    return 'border-primary-400/30 bg-dark-900/95';
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="dialogue-box fixed bottom-4 sm:bottom-8 left-0 right-0 mx-auto w-[calc(100%-1.5rem)] sm:w-[calc(100%-3rem)] md:w-full max-w-4xl px-0 sm:px-0 md:px-8 z-[100]"
        onClick={advanceNarrative}
      >
        <div
          className={`glass-effect rounded-xl sm:rounded-2xl border ${getBoxStyle()} p-4 sm:p-6 md:p-8 cursor-pointer hover:border-primary-400/50 transition-colors`}
        >
          {/* Character Info */}
          {isDialogue && currentNarrative.characterName && (
            <div className="flex items-center gap-2 sm:gap-4 mb-3 sm:mb-4">
              {/* Character Portrait */}
              {currentNarrative.characterPortraitUrl && (
                <motion.img
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  src={currentNarrative.characterPortraitUrl}
                  alt={currentNarrative.characterName}
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 border-primary-400/50 object-cover"
                />
              )}

              {/* Character Name */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-primary-300">
                  {currentNarrative.characterName}
                </h3>
              </div>
            </div>
          )}

          {/* Narrative Type Indicator */}
          {(isThought || isNarration) && (
            <div className="mb-2 sm:mb-3">
              <span className="text-xs sm:text-sm text-gray-400 italic">
                {isThought && '💭 Thought'}
                {isNarration && '📖 Narration'}
              </span>
            </div>
          )}

          {/* Narrative Text */}
          <div className="narrative-text font-serif text-white text-sm sm:text-base">
            <TypewriterText text={currentNarrative.text} />
          </div>

          {/* Continue Indicator */}
          {canAdvance && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-3 sm:mt-4 text-right"
            >
              <span className="text-xs sm:text-sm text-primary-400 animate-pulse">
                Click to continue ▼
              </span>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
